/**
 * Area unit handling.
 *
 * Field/farm areas come from the backend in square metres and are stored and
 * computed internally in hectares. Users can choose to *see* areas in hectares
 * or acres via their profile preference (`user.areaUnit`). Conversion happens
 * only at display time — nothing is stored in acres.
 */
import { useAppStore } from "stores";

export type AreaUnit = "HECTARES" | "ACRES";

export const M2_PER_HECTARE = 10000;
export const ACRES_PER_HECTARE = 2.4710538;

export const DEFAULT_AREA_UNIT: AreaUnit = "HECTARES";

/** Coerce an arbitrary stored value into a valid AreaUnit (defaults to hectares). */
export function normalizeAreaUnit(unit?: string | null): AreaUnit {
  return unit === "ACRES" ? "ACRES" : "HECTARES";
}

/** Convert a value in hectares to the chosen display unit. */
export function hectaresToUnit(hectares: number, unit: AreaUnit): number {
  return unit === "ACRES" ? hectares * ACRES_PER_HECTARE : hectares;
}

/** Convert a value in square metres to the chosen display unit. */
export function squareMetresToUnit(
  squareMetres: number,
  unit: AreaUnit
): number {
  return hectaresToUnit(squareMetres / M2_PER_HECTARE, unit);
}

/**
 * Convert a per-hectare rate (e.g. stocking rate LU/ha) to a per-unit rate.
 * For acres this divides by the acres-per-hectare factor (fewer animals per acre).
 */
export function perHectareToPerUnit(
  valuePerHectare: number,
  unit: AreaUnit
): number {
  return unit === "ACRES"
    ? valuePerHectare / ACRES_PER_HECTARE
    : valuePerHectare;
}

/**
 * Convert an absolute total applied amount to a per-hectare rate.
 * `rate = total / areaHa`. Returns null when the area is unknown or zero so
 * callers can avoid dividing by zero (e.g. before any field is selected).
 */
export function totalToRatePerHa(total: number, areaHa: number): number | null {
  if (!areaHa || areaHa <= 0) return null;
  return total / areaHa;
}

/** Convert a per-hectare rate to an absolute total: `total = rate × areaHa`. */
export function ratePerHaToTotal(rate: number, areaHa: number): number {
  return rate * areaHa;
}

/** Short unit label for display, e.g. "ha" or "ac". */
export function areaUnitLabel(unit: AreaUnit): string {
  return unit === "ACRES" ? "ac" : "ha";
}

/** Per-area unit label, e.g. "LU/ha" or "LU/ac". */
export function perAreaUnitLabel(prefix: string, unit: AreaUnit): string {
  return `${prefix}/${areaUnitLabel(unit)}`;
}

/** The current user's preferred area unit (defaults to hectares). */
export function useAreaUnit(): AreaUnit {
  return useAppStore((state) => normalizeAreaUnit(state.user?.areaUnit));
}

/**
 * Convenience hook returning the active unit plus bound conversion/label helpers,
 * so display sites can do `const { fromHa, label } = useArea();`.
 */
export function useArea() {
  const unit = useAreaUnit();
  return {
    unit,
    label: areaUnitLabel(unit),
    /** hectares -> display unit value */
    fromHa: (hectares: number) => hectaresToUnit(hectares, unit),
    /** square metres -> display unit value */
    fromM2: (squareMetres: number) => squareMetresToUnit(squareMetres, unit),
    /** per-hectare rate -> per-display-unit rate */
    perAreaFromHa: (valuePerHectare: number) =>
      perHectareToPerUnit(valuePerHectare, unit),
  };
}
